import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { MdMoreHoriz } from 'react-icons/md';
import Styled from './view-edit-delete-style';

/**
 * Actions component for generic use in Table component.
 * @param {object} props The props object.
 * @param {Array} props.actions The actions array.
 * @param {string} props.menuPlacement The menu placement ('top' or 'bottom').
 * @returns {JSX.Element}
 */
const Actions = ({ actions, menuPlacement }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popupContainerRef = useRef(null);
  const popupMenuRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const calculateHorizontalPosition = (buttonRect, estimatedMenuWidth) => {
    const spaceOnRight = window.innerWidth - buttonRect.right;
    const spaceOnLeft = buttonRect.left;

    if (spaceOnRight >= estimatedMenuWidth) {
      return { alignment: 'left', x: buttonRect.right };
    }
    if (spaceOnLeft >= estimatedMenuWidth) {
      return { alignment: 'right', x: window.innerWidth - buttonRect.left };
    }
    // Fallback: position to the right but allow overflow
    return { alignment: 'left', x: buttonRect.right };
  };

  const calculateVerticalPosition = (buttonRect, estimatedMenuHeight) => {
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    if (menuPlacement === 'top') {
      return { y: buttonRect.top, verticalPlacement: 'top' };
    }
    if (spaceBelow >= estimatedMenuHeight) {
      return { y: buttonRect.bottom, verticalPlacement: 'bottom' };
    }
    if (spaceAbove >= estimatedMenuHeight) {
      return { y: buttonRect.top, verticalPlacement: 'top' };
    }
    // Not enough space either way - default to bottom and allow overflow
    return { y: buttonRect.bottom, verticalPlacement: 'bottom' };
  };

  const togglePopup = (e) => {
    if (!isPopupOpen) {
      const buttonRect = e.target.getBoundingClientRect();
      const estimatedMenuWidth = 150; // Conservative estimate
      const estimatedMenuHeight = 160; // Conservative estimate for menu height

      const { alignment, x } = calculateHorizontalPosition(
        buttonRect,
        estimatedMenuWidth,
      );
      const { y, verticalPlacement } = calculateVerticalPosition(
        buttonRect,
        estimatedMenuHeight,
      );

      setPosition({ x, y, alignment, verticalPlacement });
      setIsPopupOpen(true);
    } else {
      setIsPopupOpen(false);
    }
  };

  const handleItemClick = () => {
    setIsPopupOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupContainerRef.current &&
        !popupContainerRef.current.contains(event.target)
      ) {
        setIsPopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Styled.PopupContainer ref={popupContainerRef}>
      <Styled.IconButton type="button" onClick={togglePopup}>
        <MdMoreHoriz />
      </Styled.IconButton>
      {isPopupOpen && (
        <Styled.PopupMenu ref={popupMenuRef} $position={position}>
          {actions.map((action) => (
            <Styled.MenuItem
              onClick={() => {
                action.onClick();
                handleItemClick();
              }}
              key={action.text ?? action.name}
            >
              {action.icon}
              <Styled.Text $textColor={action.textColor}>
                {action.text ?? action.name}
              </Styled.Text>
            </Styled.MenuItem>
          ))}
        </Styled.PopupMenu>
      )}
    </Styled.PopupContainer>
  );
};

Actions.propTypes = {
  actions: PropTypes.array.isRequired,
  menuPlacement: PropTypes.oneOf(['top', 'bottom']),
};

Actions.defaultProps = {
  menuPlacement: 'bottom',
};

export default Actions;
